import { useState } from "react";
import {
  Calculator, Check, ArrowRight, ShoppingCart, Mail, Image, Search, Share2,
  FileText, Settings, Calendar, MessageCircle, MapPin, Languages, Send,
  MessagesSquare, Star, CreditCard, Bot, Smartphone, Gauge, BarChart3,
  Film, MousePointerClick, UserCircle, Sparkles
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const addons = [
  { id: "ecommerce", label: "E-Commerce / Online Shop", price: 10000, icon: ShoppingCart, popular: true },
  { id: "contact", label: "Contact Form with Backend", price: 15000, icon: Mail, popular: true },
  { id: "gallery", label: "Image Gallery Section", price: 10000, icon: Image },
  { id: "seo", label: "SEO Setup & Optimization", price: 5000, icon: Search, popular: true },
  { id: "social", label: "Social Media Integration", price: 7000, icon: Share2 },
  { id: "blog", label: "Blog / News Section", price: 5000, icon: FileText },
  { id: "admin", label: "Admin Panel / CMS", price: 20000, icon: Settings },
  { id: "booking", label: "Booking / Appointment System", price: 10000, icon: Calendar },
  { id: "whatsapp", label: "WhatsApp Chat Button", price: 3000, icon: MessageCircle, popular: true },
  { id: "maps", label: "Google Maps Integration", price: 3000, icon: MapPin },
  { id: "multilang", label: "Multi-Language (Hindi/English)", price: 10000, icon: Languages },
  { id: "newsletter", label: "Newsletter / Email Signup", price: 5000, icon: Send },
  { id: "livechat", label: "Live Chat Widget", price: 7000, icon: MessagesSquare },
  { id: "reviews", label: "Customer Reviews System", price: 5000, icon: Star },
  { id: "payments", label: "Payment Gateway (Razorpay/UPI)", price: 10000, icon: CreditCard, popular: true },
  { id: "chatbot", label: "AI Chatbot", price: 25000, icon: Bot },
  { id: "pwa", label: "PWA (Install as App)", price: 10000, icon: Smartphone },
  { id: "speed", label: "Speed Optimization Package", price: 5000, icon: Gauge },
  { id: "analytics", label: "Analytics Dashboard", price: 7000, icon: BarChart3 },
  { id: "video", label: "Video Background / Hero Reel", price: 5000, icon: Film },
  { id: "popup", label: "Lead Capture Popup", price: 3000, icon: MousePointerClick },
  { id: "login", label: "Login / User Accounts", price: 15000, icon: UserCircle },
];

const maintenancePlans = [
  { id: "basic", label: "Basic", desc: "Content updates & minor fixes", price: 1000 },
  { id: "standard", label: "Standard", desc: "Content + Security + Backups + Support", price: 2000 },
  { id: "premium", label: "Premium", desc: "Full service + SEO + Analytics + Priority", price: 3500 },
];

export default function PricingCalculator() {
  const [pages, setPages] = useState(5);
  const [withAnimation, setWithAnimation] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [maint, setMaint] = useState("basic");

  const basePrice = 15000;
  const pageRate = withAnimation ? 3500 : 2500;
  const pagesTotal = pages * pageRate;
  const addonsTotal = selectedAddons.reduce((sum, id) => sum + (addons.find(a => a.id === id)?.price || 0), 0);
  const maintPrice = maintenancePlans.find(m => m.id === maint)?.price || 500;
  const total = basePrice + pagesTotal + addonsTotal;

  const toggleAddon = (id) => setSelectedAddons(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);

  const fmt = (n) => new Intl.NumberFormat('en-IN').format(n);

  return (
    <section id="pricing" data-testid="pricing-calculator" className="py-14 sm:py-20 lg:py-24 bg-[#141414] overflow-x-hidden scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <p className="section-label text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-red-600 justify-center">Transparent Pricing</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">
            Website <span className="text-red-600">Cost Calculator</span>
          </h2>
          <p className="text-sm text-zinc-500 mt-3 max-w-xl mx-auto">Get an instant estimate for your project. Honest pricing, no hidden charges.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Configuration */}
          <div className="lg:col-span-2 space-y-8">
            {/* Base + Pages */}
            <div className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Base Package</h3>
                <span className="text-red-500 font-black text-lg">Rs {fmt(basePrice)}</span>
              </div>
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Number of Pages: <span className="text-white font-bold text-base">{pages}</span></Label>
                  <span className="text-sm text-zinc-400">Rs {fmt(pageRate)}/page</span>
                </div>
                <Slider
                  data-testid="pages-slider"
                  value={[pages]}
                  onValueChange={(v) => setPages(v[0])}
                  min={1}
                  max={20}
                  step={1}
                  className="[&_[role=slider]]:bg-red-600 [&_[role=slider]]:border-0 [&_.range]:bg-red-600"
                />
              </div>
              <div className="flex items-center justify-between p-4 border border-white/10">
                <div>
                  <Label className="text-sm font-bold text-white">Animations & Interactions</Label>
                  <p className="text-xs text-zinc-500 mt-1">Hover effects, scroll animations, transitions</p>
                </div>
                <Switch
                  data-testid="animation-toggle"
                  checked={withAnimation}
                  onCheckedChange={setWithAnimation}
                  className="data-[state=checked]:bg-red-600"
                />
              </div>
            </div>

            {/* Add-ons */}
            <div className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white">Add-on Features</h3>
                  <p className="text-xs text-zinc-500 mt-1">Mix and match — pay only for what you need</p>
                </div>
                {selectedAddons.length > 0 && (
                  <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/40 px-3 py-1.5">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-red-500">
                      {selectedAddons.length} selected · +Rs {fmt(addonsTotal)}
                    </span>
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {addons.map(addon => {
                  const selected = selectedAddons.includes(addon.id);
                  const Icon = addon.icon || Sparkles;
                  return (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddon(addon.id)}
                      data-testid={`addon-${addon.id}`}
                      aria-pressed={selected}
                      className={`relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-2 p-4 pr-4 sm:pr-4 border text-left transition-all duration-200 cursor-pointer hover:-translate-y-0.5 ${
                        selected
                          ? 'border-red-600 bg-red-600/10 shadow-[3px_3px_0_0_rgba(229,9,20,0.9)]'
                          : 'border-white/10 hover:border-red-600/50 hover:shadow-[3px_3px_0_0_rgba(229,9,20,0.4)]'
                      }`}
                    >
                      {addon.popular && !selected && (
                        <span className="absolute -top-2 right-2 bg-red-600 text-white text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-white z-10">
                          Popular
                        </span>
                      )}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Checkbox (leading) */}
                        <div
                          role="checkbox"
                          aria-checked={selected}
                          className={`w-5 h-5 shrink-0 flex items-center justify-center border-2 transition-colors ${
                            selected
                              ? 'bg-red-600 border-red-600'
                              : 'bg-transparent border-white/30 group-hover:border-red-500'
                          }`}
                        >
                          {selected && <Check size={12} strokeWidth={3} className="text-white" />}
                        </div>
                        {/* Feature icon */}
                        <Icon size={14} className={`shrink-0 transition-colors ${selected ? 'text-red-400' : 'text-red-500'}`} />
                        <span className="text-xs font-bold uppercase tracking-wider text-zinc-300 leading-tight break-words">{addon.label}</span>
                      </div>
                      <span className={`text-xs font-bold shrink-0 self-end sm:self-auto sm:ml-2 transition-colors ${selected ? 'text-white' : 'text-red-500'}`}>
                        +Rs {fmt(addon.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Maintenance */}
            <div className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
              <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-6">Monthly Maintenance</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {maintenancePlans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => setMaint(plan.id)}
                    data-testid={`maint-${plan.id}`}
                    className={`p-4 border text-left transition-all duration-200 cursor-pointer hover:-translate-y-0.5 ${
                      maint === plan.id
                        ? 'border-red-600 bg-red-600/10 shadow-[3px_3px_0_0_rgba(229,9,20,0.9)]'
                        : 'border-white/10 hover:border-red-600/50 hover:shadow-[3px_3px_0_0_rgba(229,9,20,0.4)]'
                    }`}
                  >
                    <p className="text-sm font-bold text-white uppercase tracking-wider">{plan.label}</p>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{plan.desc}</p>
                    <p className="text-lg font-black text-red-500 mt-3">Rs {fmt(plan.price)}<span className="text-xs text-zinc-500 font-normal">/mo</span></p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 border border-red-600/30 bg-[#0A0A0A] p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-6">
                <Calculator size={18} className="text-red-500" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Your Estimate</h3>
              </div>

              <div className="space-y-3 text-sm border-b border-white/10 pb-6 mb-6">
                <div className="flex justify-between text-zinc-400">
                  <span>Base charge</span>
                  <span className="text-white">Rs {fmt(basePrice)}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>{pages} pages {withAnimation ? '(animated)' : '(static)'}</span>
                  <span className="text-white">Rs {fmt(pagesTotal)}</span>
                </div>
                {selectedAddons.map(id => {
                  const addon = addons.find(a => a.id === id);
                  return (
                    <div key={id} className="flex justify-between gap-3 text-zinc-400">
                      <span className="break-words min-w-0 flex-1">{addon?.label}</span>
                      <span className="text-white shrink-0 whitespace-nowrap">Rs {fmt(addon?.price)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between items-center gap-3 mb-2 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-zinc-400">One-time cost</span>
                <span className="text-2xl sm:text-3xl font-black text-white whitespace-nowrap">Rs {fmt(total)}</span>
              </div>
              <div className="flex justify-between items-center gap-3 mb-8 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-zinc-400">Monthly maintenance</span>
                <span className="text-base sm:text-lg font-bold text-red-500 whitespace-nowrap">Rs {fmt(maintPrice)}/mo</span>
              </div>

              <a
                href={`https://wa.me/919242478118?text=${encodeURIComponent(
`Hi Kamran! I used the pricing calculator on KoKo Sites.

Here's what I selected:

BASE CHARGE: Rs ${fmt(basePrice)}

PAGES: ${pages} pages (${withAnimation ? 'WITH animations' : 'WITHOUT animations'}) @ Rs ${fmt(pageRate)}/page = Rs ${fmt(pagesTotal)}

${selectedAddons.length > 0 ? `ADD-ONS:
${selectedAddons.map(id => {const a = addons.find(x => x.id === id); return `  - ${a?.label}: Rs ${fmt(a?.price)}`;}).join('\n')}
Add-ons Total: Rs ${fmt(addonsTotal)}` : 'ADD-ONS: None selected'}

ONE-TIME TOTAL: Rs ${fmt(total)}

MONTHLY MAINTENANCE: ${maintenancePlans.find(m => m.id === maint)?.label} - Rs ${fmt(maintPrice)}/month (${maintenancePlans.find(m => m.id === maint)?.desc})

Let's discuss my project!`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-whatsapp-cta"
                className="btn-brutal btn-shine group block w-full text-center bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-8 text-sm border-2 border-white hover:bg-white hover:text-black"
              >
                Get Started <ArrowRight className="inline ml-2 group-hover:translate-x-1 transition-transform" size={14} />
              </a>

              <p className="text-[10px] text-zinc-600 text-center mt-4">This is an estimate. Final quote may vary based on project specifics.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
